import type { SetStateAction } from 'react';

import type { StorageFileRecord } from 'hb-react/shared/hooks/useGlobalStorageV2/types';

export interface ConfirmModalProps {
  isEditingPost: boolean;
  isOpen: boolean;
  isTopicPost: boolean;
  onCloseModal: () => void;
  setDisplayConfirmModal: (value: SetStateAction<boolean>) => void;
  topicId?: number;
  userPostId?: number;
}

export interface EmbedProps {
  description: string;
  mean_alpha?: number;
  provider_name: string;
  provider_url: string;
  thumbnail_height: number;
  thumbnail_url: string;
  thumbnail_width: number;
  title: string;
  type: 'link';
  url: string;
  version: '1.0';
}

export enum FeedType {
  END_OF_FEED_TYPE = 'END_OF_FEED_TYPE',
  GROUP_FEED_TYPE = 'GROUP_FEED_TYPE',
  MY_GROUPS_FEED_TYPE = 'MY_GROUPS_FEED_TYPE',
  NETWORK_FEED_TYPE = 'NETWORK_FEED_TYPE',
}

export type FormikValues = InitialValues & {
  post: {
    storage_uploads_attachments_attributes?: Array<{
      _destroy: boolean;
      id: number;
    }>;
  };
  targeted_user_ids?: number[];
};

export interface GetInitialValuesParams {
  isTopicPost: boolean;
  topicId?: number;
  userPost?: UserPostProps;
}

export interface HandleDataParams {
  isEditingPost: boolean;
  onCloseModal: () => void;
  onCreate?: (userPost: UserPostProps) => void;
  onUpdate?: (userPost: UserPostProps) => void;
  topic?: TopicProps | null;
  type: FeedType;
}

export type ImagesProps =
  | {
      default_url: string;
      id: number;
      name: string;
    }
  | StorageFileRecord;

export interface InitialValues {
  post: {
    content_html: string;
    embed: EmbedProps | Record<string, never>;
    storage_attachments?: UploadsProps[];
    storage_uploads?: ImagesProps[];
    tag_ids?: number[];
    topic_id?: number;
  };
}

export interface MakeHandleSubmitParams {
  currentUserId: number;
  isEditingPost: boolean;
  isTopicPost: boolean;
  onCloseModal: () => void;
  onCreate?: (userPost: UserPostProps) => void;
  onUpdate?: (userPost: UserPostProps) => void;
  topic?: TopicProps | null;
  type: FeedType;
  userPostId: number;
  values: FormikValues;
}

export interface NotifyPeopleProps {
  isEditingPost: boolean;
  shouldShowNotifyPeople: boolean;
  userPost?: UserPostProps;
}

export interface OptionInput {
  id: number;
  name: string;
}

export interface QuillInputProps {
  ariaLabelledBy?: string | null;
  containerElement?: HTMLElement | null;
  initialEmbed: EmbedProps | Record<string, never>;
  onToggleEmojiPicker: (value: SetStateAction<boolean>) => void;
}

export interface SubmitButtonProps {
  isEditingPost: boolean;
}

export interface TopicProps {
  id: number;
  name: string;
  tags?: OptionInput[];
}

export interface TopicTagsProps {
  isTopicPost: boolean;
  topic: TopicProps;
}

export type UploadsProps =
  | {
      file_name: string;
      file_url: string;
      id: number;
    }
  | StorageFileRecord;

export interface UserPostFormModalProps {
  isOpen?: boolean;
  onCloseModal: () => void;
  onCreate?: (userPost: UserPostProps) => void;
  onUpdate?: (userPost: UserPostProps) => void;
  topic?: TopicProps | null;
  type: FeedType;
  userPost?: UserPostProps;
  userPostId?: number;
}

export interface UserPostProps {
  article_html: null;
  attached_images: ImagesProps[];
  content: string | null;
  content_html: string | null;
  created_at: string;
  embed: EmbedProps;
  feedable_at: string;
  from_record: Record<string, unknown>;
  id: number;
  last_contributors: unknown[];
  likes: {
    count: number;
    id: null;
    likeable_id: number;
    users: unknown[];
  };
  network_categories: unknown[];
  pinned_to_top_posts: boolean;
  published_at: string | null;
  tag_ids?: number[];
  tags?: unknown[];
  title: string | null;
  topic_id?: number;
  total_comments: number;
  type: 'posts_feed_post' | 'posts_topic_post';
  updated_at: string;
  uploads: UploadsProps[];
  user: UserProps;
  user_targeting?: {
    authorized: boolean;
    max_targeted_users: number;
  };
}

export interface UserProps {
  firstname: string;
  headline: string | null;
  id: number;
  last_seen_at?: string;
  lastname: string;
  name?: string;
  photo_thumb_url: string;
  photo_url?: string;
  prefix_name: string | null;
  suffix_name: string | null;
}
